import { Button, Box, TextField, Select, MenuItem, FormControl, InputLabel, IconButton, Typography, Divider, Chip, Fade, Paper, Tooltip, useTheme } from "@mui/material";
import { useContext, useEffect, useState, useRef } from "react";
import { DataGridContext } from "../context";
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import EqualIcon from '@mui/icons-material/DragIndicator';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';

import { GridFilterOperator, GridLinkOperator, GridFilterModel } from '@mui/x-data-grid-pro';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';

const CustomFilterPanel = (props) => {
  const { onMouseDown, isDragging, panelSize, onResizeStart, isResizing, onPanelSizeChange } = props;
  const { columnFilterModel, setColumnFilterModel, headers, data, backendHeaders, filterableFields } = useContext(DataGridContext);
  const panelRef = useRef<HTMLDivElement>(null);
  const theme = useTheme();

  // Get OS-specific keyboard shortcut text
  const getKeyboardShortcut = (key: string) => {
    const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
    return isMac ? `⌘${key}` : `Ctrl+${key}`;
  };

  // Keyboard event handler for filter panel
  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Enter key to apply filters
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleFilterApply();
    }
    // Escape key to close panel
    if (event.key === 'Escape') {
      event.preventDefault();
      props.onClose();
    }
  };

  const getOperatorsForField = (fieldName) => {
    // Find the backend header for this field
    const backendHeader = backendHeaders.find(h => 
      h.db_field === fieldName || h.input_file_field === fieldName
    );
    
    // Use the field_data_type from backend headers directly
    const dataType = backendHeader?.field_data_type || 'string';


    switch (dataType) {
      case 'date':
        return [
          { value: 'is', label: 'Is', icon: <EqualIcon fontSize="small" /> },
          { value: 'not', label: 'Is not', icon: <BlockIcon fontSize="small" /> },
          { value: 'before', label: 'Before', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'after', label: 'After', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'isEmpty', label: 'Is empty', icon: <BlockIcon fontSize="small" /> },
          { value: 'isNotEmpty', label: 'Is not empty', icon: <CheckCircleIcon fontSize="small" /> },
        ];
      
      case 'number':
        return [
          { value: '=', label: 'Equals', icon: <EqualIcon fontSize="small" /> },
          { value: '!=', label: 'Not equals', icon: <BlockIcon fontSize="small" /> },
          { value: '>', label: 'Greater than', icon: <TextFieldsIcon fontSize="small" /> },
          { value: '<', label: 'Less than', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'isEmpty', label: 'Is empty', icon: <BlockIcon fontSize="small" /> },
          { value: 'isNotEmpty', label: 'Is not empty', icon: <CheckCircleIcon fontSize="small" /> },
        ];
      
      case 'number_percent':
        return [
          { value: '=', label: 'Equals', icon: <EqualIcon fontSize="small" /> },
          { value: '!=', label: 'Not equals', icon: <BlockIcon fontSize="small" /> },
          { value: '>', label: 'Greater than', icon: <TextFieldsIcon fontSize="small" /> },
          { value: '<', label: 'Less than', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'isEmpty', label: 'Is empty', icon: <BlockIcon fontSize="small" /> },
          { value: 'isNotEmpty', label: 'Is not empty', icon: <CheckCircleIcon fontSize="small" /> },
        ];
      
      default: // string, text, etc.
        return [
          { value: 'contains', label: 'Contains', icon: <SearchIcon fontSize="small" /> },
          { value: 'equals', label: 'Equals', icon: <EqualIcon fontSize="small" /> },
          { value: 'startsWith', label: 'Starts with', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'endsWith', label: 'Ends with', icon: <TextFieldsIcon fontSize="small" /> },
          { value: 'isEmpty', label: 'Is empty', icon: <BlockIcon fontSize="small" /> },
          { value: 'isNotEmpty', label: 'Is not empty', icon: <CheckCircleIcon fontSize="small" /> },
        ];
    }
  };

  // Filter headers based on filterableFields
  const getFilterableHeaders = () => {
    if (!filterableFields || filterableFields.length === 0) {
      return headers; // Show all headers if no filterableFields specified
    }
    return headers.filter(header => filterableFields.includes(header.field));
  };

  const filterableHeaders = getFilterableHeaders();

  const [filters, setFilters] = useState(() => {
    // Initialize with existing filters or create a default filter
    const existingFilters = columnFilterModel?.items || [];
    if (existingFilters.length === 0 && filterableHeaders.length > 0) {
      const defaultField = filterableHeaders[0]?.field || '';
      
      // Get backend headers to determine field type
      const backendHeader = backendHeaders.find(h => 
        h.db_field === defaultField || h.input_file_field === defaultField
      );
      const dataType = backendHeader?.field_data_type || 'string';
      
      let defaultOperator = 'contains'; // Default for string
      if (dataType === 'number' || dataType === 'number_percent') {
        defaultOperator = '=';
      } else if (dataType === 'date') {
        defaultOperator = 'is';
      }
      
      return [{
        id: Date.now(),
        columnField: defaultField,
        operatorValue: defaultOperator,
        value: '',
      }];
    }
    return existingFilters;
  });

  // Auto-select the clicked column when panel opens
  useEffect(() => {
    if (props.columnField && filterableHeaders.length > 0) {
      // Check if the clicked column is already in filters
      const existingFilterIndex = filters.findIndex(filter => filter.columnField === props.columnField);
      
      if (existingFilterIndex === -1) {
        // Clear all existing filters and add only the clicked column filter
        const maxId = filters.length > 0 ? Math.max(...filters.map(f => Number(f.id) || 0)) : 0;
        const newFilter = {
          id: maxId + 1,
          columnField: props.columnField,
          operatorValue: 'contains',
          value: '',
        };
        setFilters([newFilter]); // Replace all filters with just this one
        
        // Scroll to the new filter after a short delay to ensure it's rendered
        setTimeout(() => {
          const newFilterElement = document.querySelector(`[data-filter-id="${newFilter.id}"]`) as HTMLElement;
          if (newFilterElement) {
            newFilterElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 100);
      } else {
        // Clear all filters except the clicked column filter
        const clickedFilter = filters[existingFilterIndex];
        setFilters([clickedFilter]); // Keep only the clicked column filter
        
        // Scroll to the existing filter
        const existingFilterElement = document.querySelector(`[data-filter-id="${clickedFilter.id}"]`) as HTMLElement;
        if (existingFilterElement) {
          existingFilterElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }
    }
  }, [props.columnField, filterableHeaders.length]);

  const [logicOperator, setLogicOperator] = useState<string>(columnFilterModel?.linkOperator || 'and');

  // Update default operator when headers are loaded
  useEffect(() => {
    if (filterableHeaders.length > 0 && filters.length > 0) {
      const firstFilter = filters[0];
      if (firstFilter.operatorValue === 'contains' && firstFilter.value === '') {
        // This is likely a default filter, update its operator
        const defaultField = firstFilter.columnField || filterableHeaders[0]?.field || '';
        
        // Get backend headers to determine field type
        const backendHeader = backendHeaders.find(h => 
          h.db_field === defaultField || h.input_file_field === defaultField
        );
        const dataType = backendHeader?.field_data_type || 'string';
        
        let appropriateOperator = 'contains'; // Default for string
        if (dataType === 'number' || dataType === 'number_percent') {
          appropriateOperator = '=';
        } else if (dataType === 'date') {
          appropriateOperator = 'is';
        }
        
        if (appropriateOperator !== firstFilter.operatorValue) {
          setFilters(prev => prev.map((filter, index) => 
            index === 0 ? { ...filter, operatorValue: appropriateOperator } : filter
          ));
        }
      }
    }
  }, [filterableHeaders, filters.length]);

  // Check if all available fields are already used
  const areAllFieldsUsed = () => {
    const usedFields = filters.map(f => f.columnField);
    return filterableHeaders.every(header => usedFields.includes(header.field));
  };

  const addFilter = () => {
    // Find the next available field that hasn't been used in existing filters
    const usedFields = filters.map(f => f.columnField);
    const nextAvailableField = filterableHeaders.find(header => 
      !usedFields.includes(header.field)
    )?.field || filterableHeaders[0]?.field || '';
    
    // Get backend headers to determine field type
    const backendHeader = backendHeaders.find(h => 
      h.db_field === nextAvailableField || h.input_file_field === nextAvailableField
    );
    const dataType = backendHeader?.field_data_type || 'string';
    
    let defaultOperator = 'contains'; // Default for string
    if (dataType === 'number' || dataType === 'number_percent') {
      defaultOperator = '=';
    } else if (dataType === 'date') {
      defaultOperator = 'is';
    }
    
    const newFilter = {
      id: Date.now(),
      columnField: nextAvailableField,
      operatorValue: defaultOperator,
      value: '',
    };
    setFilters([...filters, newFilter]);
    
    // Increase panel height by 64px when adding a filter
    if (onPanelSizeChange && panelSize) {
      onPanelSizeChange({
        ...panelSize,
        height: filters.map(f => f.columnField).length > 1 ? panelSize.height + 68 : panelSize.height + 150
      });
    }
  };

  const removeFilter = (id) => {
    // Don't allow removing the last filter, ensure at least one remains
    if (filters.length > 1) {
      setFilters(filters.filter(f => f.id !== id));
      if (onPanelSizeChange && panelSize) {
        onPanelSizeChange({
          ...panelSize,
          height: filters.map(f => f.columnField).length > 2 ? panelSize.height - 68 : panelSize.height - 150
        });
      }
    }
  };

  const updateFilter = (id, field, value) => {
    setFilters(filters.map(f => {
      if (f.id === id) {
        const updatedFilter = { ...f, [field]: value };
        
        // If column field changed, reset operator and value to appropriate defaults
        if (field === 'columnField') {
          const operators = getOperatorsForField(value);
          // Get backend headers to determine field type
          const backendHeader = backendHeaders.find(h => 
            h.db_field === value || h.input_file_field === value
          );
          const dataType = backendHeader?.field_data_type || 'string';
          
          let defaultOperator = 'contains'; // Default for string
          if (dataType === 'number' || dataType === 'number_percent') {
            defaultOperator = '=';
          } else if (dataType === 'date') {
            defaultOperator = 'is';
          }
          
          updatedFilter.operatorValue = defaultOperator;
          updatedFilter.value = ''; // Reset value when column changes
        }
        
        return updatedFilter;
      }
      return f;
    }));
  };

  const handleFilterApply = () => {
    // Filter out any filters with empty values, except for isEmpty and isNotEmpty operators
    const activeFilters = filters.filter(f => {
      // For "isEmpty" and "isNotEmpty" operators, include them even without value
      if (f.operatorValue === 'isEmpty' || f.operatorValue === 'isNotEmpty') {
        return true;
      }
      // For other operators, require a value
      return f.value && f.value.trim() !== '';
    });
    
    // Process filters and remove value field for isEmpty/isNotEmpty operators
    const processedFilters = activeFilters.map(filter => {
      // For isEmpty and isNotEmpty operators, don't include the value field
      if (filter.operatorValue === 'isEmpty' || filter.operatorValue === 'isNotEmpty') {
        const { value, ...filterWithoutValue } = filter;
        return filterWithoutValue;
      }
      
      // Find the backend header for this field
      const backendHeader = backendHeaders.find(h => 
        h.db_field === filter.columnField || h.input_file_field === filter.columnField
      );
      
      // Check if this is a number_percent field
      if (backendHeader?.field_data_type === 'number_percent') {
        const numericValue = parseFloat(filter.value);
        if (!isNaN(numericValue)) {
          // Divide by 100 for backend processing
          return {
            ...filter,
            value: (numericValue / 100).toString()
          };
        }
      }
      
      return filter;
    });
    
    const filterModel: GridFilterModel = { 
      items: processedFilters,
      linkOperator: logicOperator as GridLinkOperator
    };
    setColumnFilterModel(filterModel);
    
    // For client-side filtering, don't call API, just update the filter model
    // The DataGrid will handle the filtering internally
    if (props.rendermode === 'client') {
      // Only update the filter model, no API call
      console.log('Client-side filtering applied:', filterModel);
    } else {
      // For server-side filtering, call the API
      props.handleFilterApply(filterModel);
    }
  };

  const handleFilterReset = () => {
    // Reset to one default filter instead of clearing all
    const defaultField = filterableHeaders[0]?.field || '';
    
    // Get backend headers to determine field type
    const backendHeader = backendHeaders.find(h => 
      h.db_field === defaultField || h.input_file_field === defaultField
    );
    const dataType = backendHeader?.field_data_type || 'string';
    
    let defaultOperator = 'contains'; // Default for string
    if (dataType === 'number' || dataType === 'number_percent') {
      defaultOperator = '=';
    } else if (dataType === 'date') {
      defaultOperator = 'is';
    }
    
    const defaultFilter = {
      id: Date.now(),
      columnField: defaultField,
      operatorValue: defaultOperator,
      value: '',
    };
    
    setFilters([defaultFilter]);
    setLogicOperator('and');
    const resetModel: GridFilterModel = { 
      items: [], 
      linkOperator: 'and' as GridLinkOperator 
    };
    setColumnFilterModel(resetModel);
    
    // For client-side filtering, don't call API, just update the filter model
    if (props.rendermode === 'client') {
      // Only update the filter model, no API call
    } else {
      // For server-side filtering, call the API
      props.handleFilterApply(resetModel);
    }
  };

  const getActiveFiltersCount = () => {
    return filters.filter(f => {
      // For "isEmpty" and "isNotEmpty" operators, consider them active even without value
      if (f.operatorValue === 'isEmpty' || f.operatorValue === 'isNotEmpty') {
        return true;
      }
      // For other operators, require a value
      return f.value && f.value.trim() !== '';
    }).length;
  };

  const getValueInputComponent = (filter) => {
    // Find the backend header for this field
    const backendHeader = backendHeaders.find(h => 
      h.db_field === filter.columnField || h.input_file_field === filter.columnField
    );
    
    // Use the field_data_type from backend headers directly
    const finalDataType = backendHeader?.field_data_type || 'string';

    switch (finalDataType) {
      case 'date':
        return (
          <Box sx={{ flex: 1 }}>
            <LocalizationProvider dateAdapter={AdapterLuxon}>
              <DatePicker
                value={filter.value ? DateTime.fromISO(filter.value) : null}
                onChange={(newValue) => {
                  const dateString = newValue ? newValue.toISO() : '';
                  updateFilter(filter.id, 'value', dateString);
                }}
                              slotProps={{
                textField: {
                  size: 'small',
                  placeholder: 'Select date...',
                  sx: { 
                    flex: 1,
                    '& .MuiInputBase-input': { 
                      padding: '6px 10px',
                      fontSize: '0.75rem'
                    },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      '&:hover': {
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      },
                      '&:hover fieldset': {
                        borderColor: 'primary.main',
                      },
                    }
                  }
                },
                popper: {
                  disablePortal: false,
                  placement: 'bottom-start',
                  style: { zIndex: 9999 }
                }
              }}
              closeOnSelect={true}
              disableOpenPicker={false}
              onClose={() => {
                // Allow the date picker to close properly
                console.log('Date picker closing');
              }}
              onAccept={(newValue) => {
                // Handle date selection
                const dateString = newValue ? newValue.toISO() : '';
                updateFilter(filter.id, 'value', dateString);
                console.log('Date selected:', dateString);
              }}
              />
            </LocalizationProvider>
          </Box>
        );
      
      case 'number':
      case 'number_percent':
  return (
          <TextField
            size="small"
            type="number"
            placeholder="Enter number..."
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-input': { 
                padding: '6px 10px',
                fontSize: '0.75rem'
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />
        );
      
      default: // string, text, etc.
        return (
          <TextField
            size="small"
            placeholder="Value..."
            value={filter.value}
            onChange={(e) => updateFilter(filter.id, 'value', e.target.value)}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            sx={{ 
              flex: 1,
              '& .MuiInputBase-input': { 
                padding: '6px 10px',
                fontSize: '0.75rem'
              },
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'primary.main',
                },
              }
            }}
          />
        );
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Box>
        <Paper
          ref={panelRef}
          className="filter-panel"
          elevation={8}
          role="dialog"
          aria-labelledby="filter-panel-title"
          aria-describedby="filter-panel-description"
          tabIndex={-1}
          onKeyDown={handleKeyDown}
          sx={{
            backgroundColor: 'white',
            border: '1px solid rgba(0, 0, 0, 0.12)',
            borderRadius: '12px',
            position: 'relative',
            zIndex: 1000,
            width: panelSize?.width || 450,
            height: panelSize?.height || 200,
            minWidth: 450,
            maxWidth: 800,
            minHeight: 0,
            maxHeight: 1000,
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.1)',
            backdropFilter: 'blur(8px)',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
            '&:focus-visible': {
              outline: 'none',
            },
          }}
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header - Compact */}
          <Box
            id="filter-panel-title"
            onMouseDown={onMouseDown}
            sx={{
              backgroundColor: theme.palette.primary.main,
              color: 'white',
              padding: '12px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              borderRadius: '8px 8px 0 0',
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: theme.palette.primary.dark,
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              },
              '&:active': {
                cursor: 'grabbing',
                transform: 'translateY(0px)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.2)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FilterListIcon sx={{ fontSize: 18 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                Filter
              </Typography>
              {getActiveFiltersCount() > 0 && (
                <Chip
                  label={getActiveFiltersCount()}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    fontSize: '0.7rem',
                    borderRadius: '6px',
                    height: '20px',
                  }}
                />
              )}
              {/* <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ml: 1 }}>
                <DragIndicatorIcon sx={{ fontSize: 14, opacity: 0.8 }} />
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem', 
                  opacity: 0.8, 
                  fontWeight: 500,
                  fontStyle: 'italic'
                }}>
                  Drag to move
                </Typography>
              </Box> */}
            </Box>
            <IconButton
              size="small"
              onClick={props.onClose}
              aria-label="Close filter panel"
              sx={{ 
                color: 'white', 
                padding: '4px',
                '&:hover': { 
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  transform: 'scale(1.1)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

        {/* Logic Operator Section - Compact */}
        {filters.length > 1 && (
          <Box sx={{ 
            padding: '8px 16px', 
            borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
          }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, mb: 1, display: 'block', fontSize: '0.75rem' }}>
              Combine filters:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Chip
                label="AND"
                onClick={() => setLogicOperator('and')}
                color={logicOperator === 'and' ? 'primary' : 'default'}
                variant={logicOperator === 'and' ? 'filled' : 'outlined'}
                size="small"
                sx={{ 
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '0.7rem',
                  height: '24px',
                  '&:hover': { 
                    backgroundColor: logicOperator === 'and' ? undefined : 'rgba(255, 102, 0, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              />
              <Chip
                label="OR"
                onClick={() => setLogicOperator('or')}
                color={logicOperator === 'or' ? 'primary' : 'default'}
                variant={logicOperator === 'or' ? 'filled' : 'outlined'}
                size="small"
                sx={{ 
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '6px',
                  fontSize: '0.7rem',
                  height: '24px',
                  '&:hover': { 
                    backgroundColor: logicOperator === 'or' ? undefined : 'rgba(255, 102, 0, 0.1)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              />
            </Box>
          </Box>
        )}

        {/* Filter Items - Compact */}
        <Box 
          id="filter-panel-description"
          sx={{ 
            padding: '12px 16px', 
            height: `${filters.length > 1 ? 'calc(100% - 188.5px)' : 'calc(100% - 120px)'}`, // Adjust for header and footer
            // overflowY: 'auto',
            // overflowX: 'hidden'
          }}
        >
          {filters.length === 0 ? (
            // Empty state when no filters
            <Box sx={{ 
              textAlign: 'center', 
              py: 3,
              color: 'text.secondary',
              width: '100%'
            }}>
              <Typography variant="body2" sx={{ mb: 2, fontSize: '0.875rem' }}>
                No filters applied
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                Click "Add Filter" to start filtering your data
              </Typography>
            </Box>
          ) : (
            // Render existing filters
            filters.map((filter, index) => (
              <Box key={filter.id} data-filter-id={filter.id}>
                <Paper
                  elevation={0}
                  sx={{ 
                    p: 1.5,
                    mb: 1.5,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'rgba(255, 102, 0, 0.05)',
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                    {/* Column Selector - Compact */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Column</InputLabel>
                      <Select
                        value={filter.columnField}
                        label="Column"
                        onChange={(e) => updateFilter(filter.id, 'columnField', e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ 
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          '& .MuiSelect-select': { padding: '6px 10px' },
                          '& .MuiOutlinedInput-root': {
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            },
                          },
                        }}
                      >
                        {filterableHeaders.map((header) => (
                          <MenuItem key={header.field} value={header.field} sx={{ fontSize: '0.75rem' }}>
                            {header.headerName}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Operator Selector - Compact with Icons */}
                    <FormControl size="small" sx={{ minWidth: 100 }}>
                      <InputLabel sx={{ fontSize: '0.75rem' }}>Operator</InputLabel>
                      <Select
                        value={filter.operatorValue}
                        label="Operator"
                        onChange={(e) => updateFilter(filter.id, 'operatorValue', e.target.value)}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                        sx={{ 
                          fontSize: '0.75rem',
                          borderRadius: '6px',
                          '& .MuiSelect-select': { padding: '6px 10px' },
                          '& .MuiOutlinedInput-root': {
                            '&:hover': {
                              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            },
                          },
                        }}
                      >
                        {getOperatorsForField(filter.columnField).map((op) => (
                          <MenuItem key={op.value} value={op.value} sx={{ fontSize: '0.75rem' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {op.icon}
                              {op.label}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                            {/* Value Input - Compact */}
        <Box sx={{ flex: 1, position: 'relative' }}>
          {/* Hide value input for isEmpty and isNotEmpty operators */}
          {(filter.operatorValue !== 'isEmpty' && filter.operatorValue !== 'isNotEmpty') && 
            getValueInputComponent(filter)
          }
        </Box>

                    {/* Delete Button - Compact */}
                    <IconButton
                      size="small"
                      onClick={() => removeFilter(filter.id)}
                      onMouseDown={(e) => e.stopPropagation()}
                      aria-label={`Remove filter for ${filter.columnField}`}
                      sx={{ 
                        padding: '6px',
                        color: '#d32f2f',
                        backgroundColor: 'rgba(211, 47, 47, 0.1)',
                        borderRadius: '6px',
                        '&:hover': { 
                          backgroundColor: 'rgba(211, 47, 47, 0.2)',
                          transform: 'scale(1.1)'
                        },

                        transition: 'all 0.2s ease-in-out'
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                </Paper>
              </Box>
            ))
          )}

          {/* Add Filter Button - Compact */}
          <Button
            variant="outlined"
            size="small"
            onClick={addFilter}
            disabled={areAllFieldsUsed()}
            startIcon={<AddIcon />}
            aria-label="Add new filter"
            sx={{ 
              mt: 1,
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 600,
              borderColor: 'secondary.main',
              color: 'secondary.main',
              borderRadius: '6px',
              padding: '4px 10px',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'secondary.dark',
                backgroundColor: 'rgba(15, 40, 76, 0.1)',
                transform: 'scale(1.02)',
                boxShadow: '0 2px 8px rgba(15, 40, 76, 0.2)',
              },
              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                transform: 'none',
              },
            }}
          >
            Add Filter
          </Button>
        </Box>

        {/* Footer - Compact */}
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            padding: '12px 16px',
            borderTop: '1px solid rgba(0, 0, 0, 0.08)',
            backgroundColor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: '0 0 8px 8px',
          }}
        >
            <Button
              variant="contained"
              color="secondary"
              size="small"
              onClick={handleFilterApply}
              disabled={filters.length === 0 || getActiveFiltersCount() === 0}
              aria-label={`Apply filters. Press Enter for keyboard shortcut. ${getActiveFiltersCount()} active filters.`}
              sx={{ 
                flex: 1,
                fontSize: '0.75rem',
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '6px',
                padding: '4px 12px',
                backgroundColor: 'secondary.main',
                transition: 'transform 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: 'secondary.dark',
                  transform: 'scale(1.02)',
                },
                '&:disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.38)',
                  transform: 'none',
                },
              }}
            >
              Apply
            </Button>
        <Button
            variant="outlined"
          size="small"
            onClick={handleFilterReset}
            disabled={filters.length === 0}
            aria-label="Clear all filters"
            sx={{ 
              flex: 1,
              fontSize: '0.75rem',
              textTransform: 'none',
              fontWeight: 600,
              color: 'text.primary',
              borderColor: 'rgba(0, 0, 0, 0.12)',
              borderRadius: '6px',
              padding: '4px 12px',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.08)',
                transform: 'scale(1.02)',
              },

              '&:disabled': {
                background: 'rgba(0, 0, 0, 0.12)',
                color: 'rgba(0, 0, 0, 0.38)',
                transform: 'none',
              },
            }}
          >
            Clear
        </Button>
        </Box>
        
        {/* Resize Handle - Bottom Right Corner */}
        <Tooltip 
          title="Drag to resize filter panel" 
          placement="top"
          arrow
          sx={{
            '& .MuiTooltip-tooltip': {
              backgroundColor: 'rgba(0, 0, 0, 0.8)',
              fontSize: '0.75rem',
              fontWeight: 500,
            }
          }}
        >
          <Box
            onMouseDown={onResizeStart}
            sx={{
              position: 'absolute',
              bottom: 0,
              right: 0,
              width: 20,
              height: 20,
              cursor: 'nw-resize',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'rgba(0, 0, 0, 0.1)',
              borderRadius: '0 0 12px 0',
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.2)',
                transform: 'scale(1.1)',
              },
              '&:active': {
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                transform: 'scale(0.95)',
              },
            }}
          >
                          <AspectRatioIcon 
                sx={{ 
                  fontSize: 16,
                  color: 'rgba(0, 0, 0, 0.6)',
                  transition: 'all 0.2s ease-in-out',
                }} 
              />
          </Box>
        </Tooltip>
      </Paper>
    </Box>
  </Fade>
  );
};

export default CustomFilterPanel;
